import { MouseEvent, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleArrowDown, faCircleNotch, faEllipsis } from '@fortawesome/pro-light-svg-icons';

import IconButton from '@components/Button/IconButton';
import { Requisition } from '@customTypes/requisition';
import { IS_NATERA } from '@constants/constants';
import { formatDateForDisplay } from '@helpers/date';
import { useDownload } from '@hooks/useDownload';
import { useDownloadPatientCollatedReport } from '@hooks/useDownloadPatientCollatedReport';

import DowngradeOrderDialog from './footer/DowngradeOrderDialog';
import { OrderReceiptDialog } from './footer/OrderReceiptDialog';
import PatientDetailOrderPill from './PatientDetailOrderPill';

import './PatientDetailOrderHeader.scss';

interface PatientDetailOrderHeaderProps {
  requisition: Requisition;
  isMobile: boolean;
  isCompleted: boolean;
  canDowngrade: boolean;
  disableDowngradeOrder: boolean;
}

export default function PatientDetailOrderHeader({
  requisition,
  isMobile,
  isCompleted,
  canDowngrade,
  disableDowngradeOrder,
}: PatientDetailOrderHeaderProps) {
  const { onClinicalSummaryLetterDownload } = useDownload();
  const { isDownloadingCollatedReport, onDownloadCollatedReport } = useDownloadPatientCollatedReport(
    requisition.requisition_id,
  );

  const [showDowngradeOrder, setShowDowngradeOrder] = useState(false);
  const [showOrderReceipt, setShowOrderReceipt] = useState(false);

  const onOrderReceipt = (evt: MouseEvent) => {
    evt.preventDefault();
    setShowOrderReceipt(true);
  };

  const onDowngradeOrder = () => {
    setShowDowngradeOrder(true);
  };

  return (
    <>
      <div className='patient-detail-order-header'>
        <div className='patient-detail-order-header__row'>
          <div className='patient-detail-order-header__metadata-group'>
            <div className='patient-detail-order__metadata'>
              <p className='patient-detail-order__label'>Order ID</p>
              <p
                className={`patient-detail-order__metadata-title${
                  IS_NATERA ? '' : ' patient-detail-order__metadata-title--emphasized'
                }`}
              >
                {requisition.requisition_id}
              </p>
            </div>

            <div className='patient-detail-order__metadata'>
              <p className='patient-detail-order__label'>Order Date</p>
              <p
                className={`patient-detail-order__metadata-title${
                  IS_NATERA ? '' : ' patient-detail-order__metadata-title--emphasized'
                }`}
              >
                {formatDateForDisplay(requisition.order_date)}
              </p>
            </div>
          </div>
          <PatientDetailOrderPill requisition={requisition} />
        </div>

        <div className='patient-detail-order-header__actions'>
          <IconButton endIcon={faEllipsis} variant='light' size='sm' onClick={onOrderReceipt}>
            {isMobile ? 'Receipt' : 'Order Receipt'}
          </IconButton>

          {canDowngrade && (
            <IconButton
              endIcon={faEllipsis}
              variant='light'
              disabled={disableDowngradeOrder}
              size='sm'
              onClick={onDowngradeOrder}
            >
              {isMobile ? 'Downgrade' : 'Downgrade Order'}
            </IconButton>
          )}

          {isCompleted && !IS_NATERA && (
            <IconButton
              startIcon={isDownloadingCollatedReport ? undefined : faCircleArrowDown}
              variant='light'
              active={isDownloadingCollatedReport}
              size='sm'
              data-testid='packetButton'
              onClick={onDownloadCollatedReport}
            >
              {isDownloadingCollatedReport && (
                <FontAwesomeIcon icon={faCircleNotch} className='fa-spin patient-detail-order-header__packet-spinner' />
              )}
              {isMobile ? 'Packet' : 'Patient Packet'}
            </IconButton>
          )}

          {requisition.clinical_summary_letter && (
            <IconButton
              startIcon={faCircleArrowDown}
              variant='light'
              onClick={() => onClinicalSummaryLetterDownload(requisition)}
              size='sm'
            >
              {isMobile ? 'Clinical Summary' : 'Clinical Summary Letter'}
            </IconButton>
          )}
        </div>
      </div>

      {showOrderReceipt && <OrderReceiptDialog requisition={requisition} onClose={() => setShowOrderReceipt(false)} />}

      {showDowngradeOrder && (
        <DowngradeOrderDialog requisition={requisition} onClose={() => setShowDowngradeOrder(false)} />
      )}
    </>
  );
}
